if (!customElements.get('eren-day-night-bundle')) {
  class ErenDayNightBundle extends HTMLElement {
    connectedCallback() {
      if (this.dataset.inited === '1') return;
      this.dataset.inited = '1';

      this.productForm = this.closest('product-form');
      this.form = document.getElementById(this.dataset.productFormId);
      this.styleButtons = [...this.querySelectorAll('.eren-dnb__style-btn')];
      this.toggle = this.querySelector('[data-bundle-toggle]');
      this.singleToggle = this.querySelector('[data-single-toggle]');
      this.singlePriceEl = this.querySelector('[data-single-price]');
      this.bundleNote = this.querySelector('[data-bundle-note]');
      this.pricingEl = this.querySelector('[data-pricing]');

      this.currentVariantId = this.dataset.currentVariantId;
      this.currentPrice = parseInt(this.dataset.currentPrice, 10) || 0;
      this.discountAmount = parseInt(this.dataset.discountAmount, 10) || 15;
      this.discountCents = parseInt(this.dataset.discountCents, 10) || this.discountAmount * 100;
      this.discountCode = this.dataset.discountCode || 'BUNDLE15';
      this.lastBundlePrice = 0;

      this.partnerImage = this.querySelector('[data-partner-image]');
      this.partnerTitle = this.querySelector('[data-partner-title]');
      this.partnerPrice = this.querySelector('[data-partner-price]');
      this.totalRegular = this.querySelector('[data-total-regular]');
      this.totalBundle = this.querySelector('[data-total-bundle]');
      this.savingsText = this.querySelector('[data-savings-text]');
      this.saveAmountEl = this.querySelector('[data-save-amount]');

      if (!this.form || !this.styleButtons.length) return;

      const defaultBtn = this.querySelector('[data-default-style]')
        || this.styleButtons.find((btn) => btn.classList.contains('is-active'))
        || this.styleButtons[0];
      this.selectStyle(defaultBtn);

      this.styleButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.selectStyle(btn));
      });

      this.toggle?.addEventListener('change', () => {
        if (this.toggle.checked && this.singleToggle) {
          this.singleToggle.checked = false;
        }
        this.updatePurchaseMode();
      });

      this.singleToggle?.addEventListener('change', () => {
        if (this.singleToggle.checked && this.toggle) {
          this.toggle.checked = false;
        }
        requestAnimationFrame(() => this.updatePurchaseMode());
      });

      this.boundOnFormSubmit = (evt) => this.onFormSubmit(evt);
      this.form.addEventListener('submit', this.boundOnFormSubmit, true);

      this.onVariantChange = (evt) => {
        if (!evt.detail?.variant) return;
        if (evt.detail.form?.id !== this.dataset.productFormId) return;
        this.currentVariantId = String(evt.detail.variant.id);
        this.currentPrice = evt.detail.variant.price;
        this.updatePricing();
        requestAnimationFrame(() => this.updatePurchaseMode());
      };
      document.addEventListener('on:variant:change', this.onVariantChange);

      this.updatePurchaseMode();
    }

    disconnectedCallback() {
      if (this.form && this.boundOnFormSubmit) {
        this.form.removeEventListener('submit', this.boundOnFormSubmit, true);
      }
      if (this.onVariantChange) {
        document.removeEventListener('on:variant:change', this.onVariantChange);
      }
    }

    selectStyle(btn) {
      if (!btn) return;

      this.styleButtons.forEach((b) => {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
      });

      this.selectedPartner = {
        variantId: btn.dataset.variantId,
        price: parseInt(btn.dataset.price, 10) || 0,
        title: btn.dataset.title || '',
        image: btn.dataset.image || '',
        available: btn.dataset.available === 'true'
      };

      this.updatePartnerDisplay();
      this.updatePricing();
    }

    updatePartnerDisplay() {
      const p = this.selectedPartner;
      if (!p) return;

      this.partnerTitle.textContent = p.title.replace(/🟠|🔴/g, '').trim();
      this.partnerPrice.textContent = this.formatMoney(p.price);

      const existingImg = this.partnerImage.querySelector('img');
      if (p.image) {
        if (existingImg) {
          existingImg.src = p.image;
          existingImg.alt = p.title;
        } else {
          const img = document.createElement('img');
          img.src = p.image;
          img.alt = p.title;
          img.width = 80;
          img.height = 80;
          img.loading = 'lazy';
          this.partnerImage.appendChild(img);
        }
      }
    }

    updatePricing() {
      const p = this.selectedPartner;
      if (!p) return;

      const regular = this.currentPrice + p.price;
      const bundle = Math.max(0, regular - this.discountCents);
      this.lastBundlePrice = bundle;

      this.totalRegular.textContent = this.formatMoney(regular);
      this.totalBundle.textContent = this.formatMoney(bundle);
      this.savingsText.textContent = `Du sparst ${this.formatMoney(this.discountCents)} mit dem Bundle`;

      if (this.saveAmountEl) {
        this.saveAmountEl.textContent = `${this.formatMoney(this.discountCents)} gespart`;
      }

      if (this.singlePriceEl) {
        this.singlePriceEl.textContent = this.formatMoney(this.currentPrice);
      }

      this.updatePurchaseMode();
    }

    isBundleMode() {
      return Boolean(this.toggle?.checked && !this.singleToggle?.checked);
    }

    updatePurchaseMode() {
      const bundleMode = this.isBundleMode();

      if (this.pricingEl) {
        this.pricingEl.hidden = !bundleMode;
      }

      if (this.bundleNote) {
        this.bundleNote.hidden = !bundleMode;
      }

      this.updateAddToCartButton();
    }

    updateAddToCartButton() {
      const btn = this.form?.querySelector('[name="add"]');
      if (!btn || btn.disabled) return;

      const defaultText = btn.dataset.addToCartText || 'In den Warenkorb';

      if (this.isBundleMode()) {
        if (this.lastBundlePrice > 0) {
          btn.textContent = `${defaultText} — ${this.formatMoney(this.lastBundlePrice)}`;
        } else {
          btn.textContent = defaultText;
        }
        return;
      }

      btn.textContent = `Nur diese Brille — ${this.formatMoney(this.currentPrice)}`;
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        const format = window.theme?.settings?.moneyWithCurrencyFormat
          || window.Shopify.money_format
          || window.Shopify.money_with_currency_format;
        if (format) {
          return Shopify.formatMoney(cents, format);
        }
      }
      const currency = window.Shopify?.currency?.active || 'EUR';
      const locale = document.documentElement.lang || (currency === 'CHF' ? 'de-CH' : 'de-DE');
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(cents / 100);
    }

    getCurrentVariantId() {
      const idInput = this.form.querySelector('[name="id"]');
      return idInput?.value || this.currentVariantId;
    }

    getSubmitBtn() {
      return this.form?.querySelector('[name="add"]');
    }

    setErrorMsgState(error = false) {
      const errorEl = this.form.querySelector('.js-form-error');
      if (!errorEl) return;
      if (error) {
        errorEl.textContent = error;
        errorEl.hidden = false;
      } else {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    }

    openCartDrawer(opener) {
      if (window.theme?.settings?.cartType !== 'drawer') return;

      const tryOpen = () => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open(opener);
          return;
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          bubbles: true,
          cancelable: false,
          detail: { opener }
        }));
      };

      setTimeout(tryOpen, 0);
    }

    handleAfterCartAdd(data, sectionsToUpdate, submitBtn) {
      if (document.querySelector('.template-cart')) {
        const cartForm = document.querySelector('cart-form');
        if (cartForm?.enableAjaxUpdate) cartForm.refresh();
        return;
      }

      if (window.theme.settings.afterAddToCart === 'page') {
        setTimeout(() => {
          window.location.href = window.theme.routes.cart;
        }, 300);
        return;
      }

      const refreshCartDrawer = () => new Promise((resolve) => {
        const drawer = document.querySelector('cart-drawer');
        const form = drawer?.querySelector('cart-form');
        if (!form?.refresh) {
          resolve();
          return;
        }
        const onMerged = () => {
          form.removeEventListener('on:cart:after-merge', onMerged);
          resolve();
        };
        form.addEventListener('on:cart:after-merge', onMerged, { once: true });
        if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
          sectionsToUpdate.forEach((el) => {
            if (typeof el.updateFromCartChange === 'function') {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          form.refresh();
        }
        setTimeout(resolve, 4000);
      });

      if (window.theme.settings.afterAddToCart === 'notification') {
        const template = document.getElementById('AddedNotification')?.content?.firstElementChild;
        if (template) {
          const notification = template.cloneNode(true);
          notification.dataset.productTitle = 'Bundle';
          (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
        }
      } else if (window.theme.settings.afterAddToCart === 'drawer') {
        refreshCartDrawer().then(() => this.openCartDrawer(submitBtn));
      }
    }

    async onFormSubmit(evt) {
      if (!this.isBundleMode() || !this.selectedPartner?.variantId) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      const submitBtn = this.getSubmitBtn();
      if (!submitBtn || submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (!this.selectedPartner.available) {
        this.setErrorMsgState('Die gewählte Bundle-Brille ist derzeit nicht verfügbar.');
        return;
      }

      this.setErrorMsgState();

      const qtyInput = this.form.querySelector('[name="quantity"]');
      const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      const currentVariantId = parseInt(this.getCurrentVariantId(), 10);
      const partnerVariantId = parseInt(this.selectedPartner.variantId, 10);

      if (!currentVariantId || !partnerVariantId) {
        this.setErrorMsgState('Bundle konnte nicht hinzugefügt werden. Bitte Seite neu laden.');
        return;
      }

      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');

      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el);
      const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

      const payload = {
        items: [
          {
            id: currentVariantId,
            quantity,
            properties: { _bundle: 'tag-nacht' }
          },
          {
            id: partnerVariantId,
            quantity,
            properties: { _bundle: 'tag-nacht' }
          }
        ]
      };

      if (sectionIds.length) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }

      let data;

      try {
        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });

        data = await response.json();
        if (!response.ok) {
          let error = data.description || data.message || 'Bundle konnte nicht hinzugefügt werden.';
          if (data.errors && typeof data.errors === 'object') {
            error = Object.entries(data.errors).map((item) => item[1].join(', ')).join(', ');
          }
          throw new Error(error);
        }

        if (this.discountCode) {
          await fetch(`/discount/${encodeURIComponent(this.discountCode)}`, {
            method: 'GET',
            credentials: 'same-origin'
          });
        }
      } catch (err) {
        this.setErrorMsgState(err.message);
        submitBtn.classList.remove('is-loading');
        submitBtn.classList.remove('is-success');
        submitBtn.removeAttribute('aria-disabled');
        return;
      }

      this.productForm?.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          variantId: currentVariantId,
          bundle: true
        }
      }));

      this.handleAfterCartAdd(data, sectionsToUpdate, submitBtn);

      submitBtn.classList.add('is-success');
      setTimeout(() => {
        submitBtn.classList.remove('is-loading');
        submitBtn.classList.remove('is-success');
      }, 2000);
      submitBtn.removeAttribute('aria-disabled');
    }
  }

  customElements.define('eren-day-night-bundle', ErenDayNightBundle);
}
